import { useQuery, useQueryClient } from "@tanstack/react-query";
import { OptionSelect } from "Common/Components";
import { useSessionStore } from "Common/Stores";
import { useFormik } from "formik";
import {
  validateSelect,
  validateYupStringWithSpecialCharacters,
} from "helpers/functionsHelpers";
import { NotificationType, showNotifications } from "helpers/toas_notification";
import { PostTransfer, ProductTransferPost, warehouseListI } from "interfaces";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { createTransfer, getWarehouse, getWarehouseByBranch } from "services";
import * as Yup from "yup";
import { useTransferWarehouseStore } from "../stores/storeTransferWarehouse";

export const useTransferForm = () => {
  const { t } = useTranslation();

  const { session } = useSessionStore();

  const queryClient = useQueryClient();

  const {
    setPayloadTransfer,
    clearAllStore,
    payloadTransfer,
    productsTransfer,
  } = useTransferWarehouseStore();

  const [originRows, setOriginRows] = useState<OptionSelect[]>([]);
  const [destinyRows, setDestinyRows] = useState<OptionSelect[]>([]);

  const branchRef = useRef<string>(String(session?.employe.branch.id || ""));

  const existProductsInStore = useMemo(() => {
    return productsTransfer.length > 0;
  }, [productsTransfer]);

  const initialValues = {
    origin: "",
    destiny: "",
    observations: "",
  };

  const validationSchema = Yup.object().shape({
    origin: validateSelect(t),
    destiny: validateSelect(t),
    observations: validateYupStringWithSpecialCharacters(t, true),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!existProductsInStore) {
        showNotifications(
          NotificationType.ERROR,
          t("transferBetweenWarehouse.productStoreEmpty")
        );
        return;
      }

      const products: ProductTransferPost[] = productsTransfer.map((item) => {
        return {
          productCode: item.product?.productCode.productCode || "",
          quantity: item.quantity,
        } as ProductTransferPost;
      });

      const payload: PostTransfer = {
        warehousefrom_id: Number(values.origin),
        warehouseto_id: Number(values.destiny),
        observation: values.observations,
        productTransfer: products,
      };

      try {
        const respTransfer = await createTransfer(payload);

        if (respTransfer.code === "200") {
          showNotifications(
            NotificationType.SUCCESS,
            t("transferBetweenWarehouse.transferSuccess")
          );

          formik.resetForm();
          clearAllStore();
          queryClient.invalidateQueries({
            queryKey: ["productsByWarehouse"],
          });
        } else {
          showNotifications(
            NotificationType.ERROR,
            t("transferBetweenWarehouse.transferError") +
              " " +
              respTransfer.message
          );
        }
      } catch (error) {
        showNotifications(
          NotificationType.ERROR,
          t("transferBetweenWarehouse.transferError") + " " + error
        );
      }
    },
  });

  const { data: warehousesDataDestiny } = useQuery({
    queryKey: ["warehouses", "destiny"],
    queryFn: getWarehouse,
  });

  const { data: warehousesData } = useQuery({
    queryKey: ["warehouses"],
    queryFn: () => getWarehouseByBranch(branchRef.current),
    enabled: !!branchRef.current,
  });

  useEffect(() => {
    if (warehousesDataDestiny) {
      let destiny = warehousesDataDestiny;

      if (formik.values.origin) {
        destiny = warehousesDataDestiny.filter(
          (item) => item.id !== Number(formik.values.origin)
        );
      }

      setDestinyRows(createRows(destiny));
    }
  }, [warehousesDataDestiny, formik.values]);

  useEffect(() => {
    if (warehousesData) {
      setOriginRows(createRows(warehousesData));
    }
  }, [warehousesData]);

  const createRows = (data: warehouseListI[]) => {
    return data.map((item) => {
      return {
        label: item.description,
        value: item.id.toString(),
      };
    });
  };

  return {
    // * Properties
    t,
    originRows,
    destinyRows,
    formik,
    payloadTransfer,
    existProductsInStore,

    // * Methods
    setPayloadTransfer,
  };
};
